import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatDateFR, getNowParis, getDateISOParis } from "../lib/utils";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fonction pour vérifier les stocks faibles (exportée)
export const hasLowStock = async () => {
  try {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await axios.get(`${BACKEND_URL}/api/product-catalog`);
    const products = response.data;
    return products.some(p => p.quantite_stock < 2);
  } catch (error) {
    console.error("Erreur vérification stock:", error);
    return false;
  }
};

const StockClients = () => {
  // État pour les tabs (par défaut: panier)
  const [activeTab, setActiveTab] = useState('panier'); // 'catalogue', 'panier', 'historique'
  
  const [consumables, setConsumables] = useState([]);
  const [clients, setClients] = useState([]);
  const [housings, setHousings] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [precommandes, setPrecommandes] = useState([]); // Précommandes en attente (panier)
  const [precommandesValidees, setPrecommandesValidees] = useState([]); // Précommandes validées (historique)
  const [commandes, setCommandes] = useState([]); // Historique des commandes
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [showModal, setShowModal] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPrecommande, setEditingPrecommande] = useState(null);
  const [showCommandeModal, setShowCommandeModal] = useState(false);
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [formData, setFormData] = useState({
    client_id: "",
    housing_id: "",
    date_fourniture: getDateISOParis(),
    consommable_nom: "",
    quantite: "1",
    prix: "",
    use_catalog: true
  });
  const [catalogForm, setCatalogForm] = useState({
    nom_produit: "",
    quantite_stock: "0",
    prix_achat: "" // Prix unique (anciennement prix d'achat)
  });
  const [monthlyTotal, setMonthlyTotal] = useState({});
  
  // Nouveaux états pour le système d'achat
  const [cart, setCart] = useState([]); // Panier d'achats
  const [includeTVA, setIncludeTVA] = useState(true); // Choix TVA
  const [cartQuantities, setCartQuantities] = useState({}); // Quantités modifiables par produit

  function getCurrentMonth() {
    const now = getNowParis();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  useEffect(() => {
    loadData();
    checkEndOfMonthReminder();
  }, [selectedMonth]);

  // Rafraîchir le Panier automatiquement (toutes les 60s et au retour sur l'onglet)
  useEffect(() => {
    const refreshPanier = async () => {
      if (document.hidden) return;
      try {
        const res = await axios.get(`${API}/precommandes/pending`);
        setPrecommandes(res.data);
      } catch (error) {
        console.error("Erreur rafraîchissement panier:", error);
      }
    };
    const interval = setInterval(refreshPanier, 60 * 1000);
    document.addEventListener("visibilitychange", refreshPanier);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshPanier);
    };
  }, []);

  // Vérifier les stocks faibles après chargement des données
  useEffect(() => {
    if (productCatalog.length > 0) {
      checkLowStock();
    }
  }, [productCatalog]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [consumablesRes, clientsRes, housingsRes, catalogRes, precommandesRes, precommandesValideesRes, commandesRes] = await Promise.all([
        axios.get(`${API}/consumables?month=${selectedMonth}`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/housings`),
        axios.get(`${API}/product-catalog`),
        axios.get(`${API}/precommandes/pending`),
        axios.get(`${API}/precommandes/validated`), // Précommandes validées pour l'historique
        axios.get(`${API}/commandes`) // Charger l'historique des commandes
      ]);
      
      console.log("Clients chargés:", clientsRes.data.length);
      console.log("Logements chargés:", housingsRes.data.length);
      console.log("Catalogue produits chargé:", catalogRes.data.length);
      console.log("Précommandes en attente:", precommandesRes.data.length);
      console.log("Précommandes validées:", precommandesValideesRes.data.length);
      console.log("Commandes chargées:", commandesRes.data.length);
      
      setConsumables(consumablesRes.data);
      setClients(clientsRes.data);
      setHousings(housingsRes.data);
      setProductCatalog(catalogRes.data);
      setPrecommandes(precommandesRes.data);
      setPrecommandesValidees(precommandesValideesRes.data);
      setCommandes(commandesRes.data);
      calculateMonthlyTotals(consumablesRes.data);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      toast.error("Erreur lors du chargement des données. Vérifiez la console.");
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyTotals = (data) => {
    const totals = {};
    data.forEach(item => {
      const key = `${item.client_id}-${item.housing_id}`;
      if (!totals[key]) {
        totals[key] = 0;
      }
      // Calcul du total : prix × quantité
      const quantite = item.quantite || 1;
      const prix = item.prix || 0;
      totals[key] += prix * quantite;
    });
    setMonthlyTotal(totals);
  };

  const checkEndOfMonthReminder = () => {
    const today = getNowParis();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const daysUntilEndOfMonth = lastDay.getDate() - today.getDate();
    
    // Rappel 3 jours avant la fin du mois
    if (daysUntilEndOfMonth <= 3 && daysUntilEndOfMonth >= 0) {
      const totalGeneral = Object.values(monthlyTotal).reduce((sum, val) => sum + val, 0);
      if (totalGeneral > 0) {
        // Afficher une notification
        showReminderNotification(totalGeneral, daysUntilEndOfMonth);
      }
    }
  };

  const showReminderNotification = (total, daysLeft) => {
    const message = `🔔 Rappel Facturation : ${daysLeft} jour${daysLeft > 1 ? 's' : ''} avant la fin du mois. Total des consommables : ${total.toFixed(2)}€`;
    
    // Vérifier si une notification a déjà été affichée aujourd'hui
    const lastNotif = localStorage.getItem('lastConsumableNotif');
    const today = getNowParis().toDateString();
    
    if (lastNotif !== today) {
      toast.info(message);
      localStorage.setItem('lastConsumableNotif', today);
    }
  };

  const checkLowStock = () => {
    const lowStockProducts = productCatalog.filter(p => p.quantite_stock < 2);
    
    if (lowStockProducts.length > 0) {
      // Vérifier si on a déjà affiché l'alerte aujourd'hui
      const lastStockAlert = localStorage.getItem('lastStockAlert');
      const today = getNowParis().toDateString();
      
      if (lastStockAlert !== today) {
        const productList = lowStockProducts.map(p => 
          `  • ${p.nom_produit}: ${p.quantite_stock} en stock`
        ).join('\n');
        
        toast.warning(`ALERTE STOCK FAIBLE\n\nLes produits suivants ont un stock inférieur à 2 :\n\n${productList}\n\nPensez à réapprovisionner !`);
        localStorage.setItem('lastStockAlert', today);
      }
    }
  };

  // Gérer la sélection d'un produit du catalogue
  const handleProductSelect = (productId) => {
    if (!productId) {
      setFormData({...formData, consommable_nom: "", prix: ""});
      return;
    }
    
    const product = productCatalog.find(p => p.id === productId);
    if (product) {
      setFormData({
        ...formData,
        consommable_nom: product.nom_produit,
        prix: product.prix_vente.toString()
      });
    }
  };

  // Ajouter un produit au catalogue
  const handleAddProductToCatalog = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/product-catalog`, {
        nom_produit: catalogForm.nom_produit,
        quantite_stock: parseInt(catalogForm.quantite_stock),
        prix_achat: parseFloat(catalogForm.prix_achat),
        prix_vente: parseFloat(catalogForm.prix_achat) // Prix de vente = prix d'achat
      });
      
      setShowCatalogModal(false);
      setCatalogForm({ nom_produit: "", quantite_stock: "0", prix_achat: "" });
      setEditingProduct(null);
      await loadData(); // Recharger pour avoir les prix à jour
      toast.success("Produit ajouté au catalogue");
    } catch (error) {
      console.error("Erreur lors de l'ajout au catalogue:", error);
      toast.error("Erreur lors de l'ajout du produit");
    }
  };

  // Modifier un produit du catalogue
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setCatalogForm({
      nom_produit: product.nom_produit,
      quantite_stock: product.quantite_stock.toString(),
      prix_achat: product.prix_achat.toString()
    });
    setShowCatalogModal(true);
  };

  const handleUpdateProductCatalog = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/product-catalog/${editingProduct.id}`, {
        nom_produit: catalogForm.nom_produit,
        quantite_stock: parseInt(catalogForm.quantite_stock),
        prix_achat: parseFloat(catalogForm.prix_achat),
        prix_vente: parseFloat(catalogForm.prix_achat) // Prix de vente = prix d'achat
      });
      
      setShowCatalogModal(false);
      setCatalogForm({ nom_produit: "", quantite_stock: "0", prix_achat: "" });
      setEditingProduct(null);
      await loadData(); // Recharger pour avoir les prix à jour
      toast.success("Produit modifié");
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  // Supprimer un produit du catalogue
  const handleDeleteProductCatalog = async (productId) => {
    {  // Action directe sans confirmation
      try {
        await axios.delete(`${API}/product-catalog/${productId}`);
        await loadData(); // Recharger pour avoir les prix à jour
        toast.success("Produit supprimé du catalogue");
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  // Fonctions pour gérer les commandes
  const handleViewCommande = (commande) => {
    setSelectedCommande(commande);
    setShowCommandeModal(true);
  };

  const handleDeleteCommande = async (commandeId) => {
    // Action directe sans confirmation

    try {
      await axios.delete(`${API}/commandes/${commandeId}`);
      toast.success("Commande supprimée avec succès");
      await loadData();
    } catch (error) {
      console.error("Erreur suppression commande:", error);
      toast.error("Erreur lors de la suppression : " + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateCommande = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/commandes/${selectedCommande.id}`, selectedCommande);
      toast.success("Commande mise à jour avec succès");
      setShowCommandeModal(false);
      setSelectedCommande(null);
      await loadData();
    } catch (error) {
      console.error("Erreur mise à jour commande:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Fonction pour marquer un produit comme livré/non livré
  const handleToggleDelivered = async (commandeId, itemIndex, isDelivered) => {
    try {
      // Trouver la commande
      const commande = commandes.find(c => c.id === commandeId);
      if (!commande) return;

      // Mettre à jour l'item localement
      const updatedItems = [...commande.items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        delivered: isDelivered,
        delivered_at: isDelivered ? new Date().toISOString() : null
      };

      // Mettre à jour la commande
      const updatedCommande = {
        ...commande,
        items: updatedItems
      };

      // Sauvegarder en base
      await axios.put(`${API}/commandes/${commandeId}`, updatedCommande);

      // Si le produit est marqué comme livré, on peut le préparer pour la facturation
      if (isDelivered) {
        const item = updatedItems[itemIndex];
        const housing = housings.find(h => h.id === commande.housing_id);
        const client = housing ? clients.find(c => c.id === housing.client_id) : clients.find(c => c.id === commande.client_id);
        
        console.log(`✅ Produit "${item.product_nom}" marqué comme livré pour ${housing?.pseudo || 'logement'}`);
        
        // Créer automatiquement une entrée dans les consommables pour facturation
        if (client && housing) {
          try {
            await axios.post(`${API}/consumables`, {
              client_id: client.id,
              housing_id: housing.id,
              date_fourniture: new Date().toISOString().split('T')[0],
              consommable_nom: item.product_nom || item.nom,
              quantite: item.quantite,
              prix: item.prix_unitaire,
              source: 'stock_delivery',
              commande_id: commandeId,
              commande_item_index: itemIndex
            });
            console.log(`📝 Ajouté en facturation pour ${client.prenom} ${client.nom} - ${housing.pseudo}`);
          } catch (err) {
            console.log("Info: Entrée facturation existante ou non nécessaire", err);
          }
        }
      } else {
        // Si on décoche, supprimer l'entrée de facturation associée
        try {
          await axios.delete(`${API}/consumables/by-source`, {
            data: {
              source: 'stock_delivery',
              commande_id: commandeId,
              commande_item_index: itemIndex
            }
          });
          console.log(`🗑️ Entrée facturation supprimée`);
        } catch (err) {
          // Pas grave si ça n'existe pas
        }
      }

      // Recharger les données
      await loadData();
      
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Erreur lors de la mise à jour du statut de livraison");
    }
  };

  // === GESTION DES PRÉ-COMMANDES ===
  
  // Créer des précommandes à partir du texte libre (détection automatique)
  const handleSubmitQuickOrder = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || !formData.housing_id || !formData.consommable_nom) {
      toast.warning("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    try {
      // Appeler le nouvel endpoint pour créer les précommandes depuis le texte
      const response = await axios.post(`${API}/precommandes/create-from-text`, null, {
        params: {
          client_id: formData.client_id,
          housing_id: formData.housing_id,
          remarque: formData.consommable_nom,
          date_fourniture: formData.date_fourniture,
          added_by: "Gérant"
        }
      });
      
      const count = response.data.count || 0;
      
      if (count > 0) {
        toast.success(`${count} précommande(s) créée(s) avec succès !\n\nLes précommandes sont en attente de validation dans la section ci-dessous.`);
      } else {
        toast.info("Aucun produit détecté dans votre texte. Essayez d'être plus précis (ex: '2 papier toilette, 1 savon')");
      }
      
      // Réinitialiser le formulaire
      setFormData({
        client_id: "",
        housing_id: "",
        date_fourniture: getDateISOParis(),
        consommable_nom: "",
        quantite: "1",
        prix: "",
        use_catalog: true
      });
      
      // Fermer le modal et recharger les données
      setShowModal(false);
      await loadData();
      
    } catch (error) {
      console.error("Erreur lors de la création des précommandes:", error);
      toast.error("Erreur lors de la création des précommandes");
    }
  };
  
  const handleValidatePrecommande = async (precommande) => {
    const produit_nom = editingPrecommande?.id === precommande.id 
      ? editingPrecommande.produit_nom 
      : precommande.produit_nom;
    const quantite = editingPrecommande?.id === precommande.id 
      ? editingPrecommande.quantite 
      : precommande.quantite;
    
    // Chercher le prix dans le catalogue si pas de prix_estime
    const findPriceInCatalog = (name) => {
      if (!name || !productCatalog.length) return null;
      const searchName = name.toLowerCase().trim();
      let found = productCatalog.find(p => p.nom_produit?.toLowerCase().trim() === searchName);
      if (!found) {
        found = productCatalog.find(p => 
          p.nom_produit?.toLowerCase().includes(searchName) ||
          searchName.includes(p.nom_produit?.toLowerCase())
        );
      }
      return found?.prix_vente || found?.prix_achat || null;
    };
    
    let prix = editingPrecommande?.id === precommande.id 
      ? editingPrecommande.prix 
      : precommande.prix_estime || findPriceInCatalog(produit_nom) || 0;

    if (!prix || prix <= 0) {
      toast.warning("Veuillez entrer un prix valide avant de valider.\n\nCliquez sur 'Modifier' pour ajouter un prix, ou ajoutez ce produit au catalogue.");
      return;
    }

    // Validation optimiste : déplacer immédiatement vers historique
    setPrecommandes(prev => prev.filter(pc => pc.id !== precommande.id));
    try {
      await axios.put(`${API}/precommandes/${precommande.id}/validate`, null, {
        params: { 
          produit_nom: produit_nom,
          quantite: quantite, 
          prix: prix 
        }
      });
      setEditingPrecommande(null);
      // Recharger pour mettre à jour l'historique
      const valRes = await axios.get(`${API}/precommandes/validated`);
      setPrecommandesValidees(valRes.data);
      toast.success("Pré-commande validée");
    } catch (error) {
      console.error("Erreur lors de la validation:", error);
      toast.error("Erreur lors de la validation");
      await loadData();
    }
  };

  const handleRejectPrecommande = async (precommande) => {
    // Suppression optimiste : retirer immédiatement de la liste locale
    setPrecommandes(prev => prev.filter(pc => pc.id !== precommande.id));
    try {
      await axios.put(`${API}/precommandes/${precommande.id}/reject`);
    } catch (error) {
      console.error("Erreur lors du rejet:", error);
      // En cas d'erreur, recharger les données pour remettre l'état correct
      await loadData();
    }
  };

  // Transférer la remarque vers la prochaine mission du logement
  const handleTransferToNextMission = async (precommande) => {
    const housingName = precommande.housing?.pseudo || precommande.housing?.adresse || 'ce logement';
    const remarque = precommande.remarque_origine;
    
    if (!remarque) {
      toast.warning("Aucune remarque à transférer");
      return;
    }
    
    if (!precommande.housing_id) {
      toast.warning("Pas de logement associé à cette pré-commande");
      return;
    }
    
    // Optimiste : retirer immédiatement
    setPrecommandes(prev => prev.filter(pc => pc.id !== precommande.id));
    try {
      await axios.post(`${API}/housings/${precommande.housing_id}/add-note-for-next`, null, {
        params: { note: remarque }
      });
      await axios.put(`${API}/precommandes/${precommande.id}/reject`);
      toast.success(`Remarque transférée vers "${housingName}"`);
    } catch (error) {
      console.error("Erreur lors du transfert:", error);
      toast.error("Erreur: " + (error.response?.data?.detail || error.message));
      await loadData();
    }
  };

  // Remettre une précommande validée dans le panier
  const handleRevertToCart = async (precommande) => {
    // Optimiste : retirer de l'historique
    setPrecommandesValidees(prev => prev.filter(pc => pc.id !== precommande.id));
    try {
      await axios.put(`${API}/precommandes/${precommande.id}/revert-to-pending`);
      const pendingRes = await axios.get(`${API}/precommandes/pending`);
      setPrecommandes(pendingRes.data);
      toast.success("Produit remis dans le panier");
    } catch (error) {
      console.error("Erreur lors du revert:", error);
      toast.error("Erreur: " + (error.response?.data?.detail || error.message));
      await loadData();
    }
  };

  // Annuler une validation (rejeter la précommande)
  const handleCancelValidation = async (precommande) => {
    // Optimiste : retirer de l'historique
    setPrecommandesValidees(prev => prev.filter(pc => pc.id !== precommande.id));
    try {
      await axios.put(`${API}/precommandes/${precommande.id}/reject`);
      toast.success("Précommande annulée");
    } catch (error) {
      console.error("Erreur lors de l'annulation:", error);
      toast.error("Erreur: " + (error.response?.data?.detail || error.message));
      await loadData();
    }
  };

  const handleEditPrecommande = (precommande) => {
    // Chercher le prix dans le catalogue si pas de prix_estime
    let prix = precommande.prix_estime;
    if (!prix || prix <= 0) {
      const searchName = precommande.produit_nom?.toLowerCase().trim();
      if (searchName && productCatalog.length) {
        // Recherche exacte d'abord
        let found = productCatalog.find(p => 
          p.nom_produit?.toLowerCase().trim() === searchName
        );
        // Sinon recherche partielle
        if (!found) {
          found = productCatalog.find(p => 
            p.nom_produit?.toLowerCase().includes(searchName) ||
            searchName.includes(p.nom_produit?.toLowerCase())
          );
        }
        if (found) {
          prix = found.prix_vente || found.prix_achat || 0;
        }
      }
    }
    
    setEditingPrecommande({
      id: precommande.id,
      produit_nom: precommande.produit_nom,
      quantite: precommande.quantite,
      prix: prix || 0
    });
  };

  const handleCancelEditPrecommande = () => {
    setEditingPrecommande(null);
  };

  const handleUpdatePrecommandeField = (field, value) => {
    setEditingPrecommande({
      ...editingPrecommande,
      [field]: field === 'quantite' ? parseInt(value) : field === 'produit_nom' ? value : parseFloat(value)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/consumables`, {
        client_id: formData.client_id,
        housing_id: formData.housing_id,
        date_fourniture: formData.date_fourniture,
        consommable_nom: formData.consommable_nom,
        quantite: parseInt(formData.quantite),
        prix: parseFloat(formData.prix)
      });
      
      setShowModal(false);
      setFormData({
        client_id: "",
        housing_id: "",
        date_fourniture: getDateISOParis(),
        consommable_nom: "",
        quantite: "1",
        prix: "",
        use_catalog: true
      });
      await loadData();
      toast.success("Consommable ajouté avec succès");
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);
      toast.error("Erreur lors de l'ajout du consommable");
    }
  };

  const [editingConsumable, setEditingConsumable] = useState(null);
  const [showEditConsumableModal, setShowEditConsumableModal] = useState(false);
  
  const handleEditConsumable = (consumable) => {
    setEditingConsumable(consumable);
    setFormData({
      client_id: consumable.client_id,
      housing_id: consumable.housing_id,
      date_fourniture: consumable.date_fourniture,
      consommable_nom: consumable.consommable_nom,
      quantite: (consumable.quantite || 1).toString(),
      prix: consumable.prix.toString(),
      use_catalog: false
    });
    setShowEditConsumableModal(true);
  };

  const handleUpdateConsumable = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/consumables/${editingConsumable.id}`, {
        consommable_nom: formData.consommable_nom,
        quantite: parseInt(formData.quantite),
        prix: parseFloat(formData.prix),
        date_fourniture: formData.date_fourniture
      });
      
      setShowEditConsumableModal(false);
      setEditingConsumable(null);
      setFormData({
        client_id: "",
        housing_id: "",
        date_fourniture: getDateISOParis(),
        consommable_nom: "",
        quantite: "1",
        prix: "",
        use_catalog: true
      });
      await loadData();
      toast.success("Consommable modifié");
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  const handleDelete = async (id) => {
    {  // Action directe sans confirmation
      try {
        await axios.delete(`${API}/consumables/${id}`);
        await loadData();
        toast.success("Consommable supprimé");
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  // Grouper les consommables par client et logement
  const groupedConsumables = {};
  consumables.forEach(item => {
    const key = `${item.client_id}-${item.housing_id}`;
    if (!groupedConsumables[key]) {
      groupedConsumables[key] = {
        client_id: item.client_id,
        housing_id: item.housing_id,
        items: []
      };
    }
    groupedConsumables[key].items.push(item);
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  const monthName = new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="max-w-full mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">🧴 Stock Clients - Consommables</h1>
            <p className="text-gray-600 mt-2">
              Gestion des consommables et commandes
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary"
          >
            🛒 Achats pour le logement
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('catalogue')}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'catalogue'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            📦 Catalogue ({productCatalog.length})
          </button>
          <button
            onClick={() => setActiveTab('panier')}
            className={`px-6 py-2 rounded-lg font-medium transition-all relative ${
              activeTab === 'panier'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            🛒 Panier ({precommandes.length})
            {precommandes.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 flex items-center justify-center rounded-full">
                {precommandes.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('historique')}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'historique'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            📋 Historique ({precommandesValidees.length})
          </button>
        </div>
      </div>

      {/* TAB PANIER - PRÉ-COMMANDES EN ATTENTE - REGROUPÉES PAR CLIENT/LOGEMENT */}
      {activeTab === 'panier' && (
        <div className="space-y-4">
          {precommandes.length > 0 ? (
            <>
              {/* Regrouper les précommandes par client/logement */}
              {(() => {
                const grouped = precommandes.reduce((acc, pc) => {
                  const key = `${pc.client_id}_${pc.housing_id}`;
                  if (!acc[key]) {
                    acc[key] = {
                      client: clients.find(c => c.id === pc.client_id),
                      housing: housings.find(h => h.id === pc.housing_id),
                      items: []
                    };
                  }
                  acc[key].items.push(pc);
                  return acc;
                }, {});

                return Object.entries(grouped).map(([key, group]) => (
                  <div key={key} className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-yellow-400">
                    {/* En-tête client/logement */}
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            🏠 {group.housing ? (group.housing.pseudo || group.housing.adresse) : 'Logement inconnu'}
                          </h3>
                          <p className="text-yellow-100 text-sm">
                            👤 {group.client ? `${group.client.prenom} ${group.client.nom}` : 'Client inconnu'}
                          </p>
                        </div>
                        <span className="bg-white text-yellow-600 px-3 py-1 rounded-full text-sm font-bold">
                          {group.items.length} produit(s)
                        </span>
                      </div>
                    </div>

                    {/* Liste des produits */}
                    <div className="divide-y divide-gray-100">
                      {group.items
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((pc) => {
                          const isEditing = editingPrecommande?.id === pc.id;
                          const catalogProduct = productCatalog.find(p => 
                            p.nom_produit?.toLowerCase().includes(pc.produit_nom?.toLowerCase()) ||
                            pc.produit_nom?.toLowerCase().includes(p.nom_produit?.toLowerCase())
                          );
                          const displayPrice = pc.prix_estime || catalogProduct?.prix_vente || 0;

                          return (
                            <div key={pc.id} className="p-3 hover:bg-yellow-50 transition-colors">
                              <div className="flex items-center gap-3">
                                {/* Date de création + ajouté par */}
                                <div className="text-xs text-gray-400 flex-shrink-0">
                                  <div>{(pc.date_creation || pc.created_at) ? new Date(pc.date_creation || pc.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}</div>
                                  {pc.added_by && <div className="text-[10px] text-purple-500">{pc.added_by}</div>}
                                </div>

                                {/* Produit et quantité */}
                                <div className="flex-1 min-w-0">
                                  {isEditing ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={editingPrecommande.produit_nom}
                                        onChange={(e) => handleUpdatePrecommandeField('produit_nom', e.target.value)}
                                        className="flex-1 px-2 py-1 border rounded text-sm"
                                      />
                                      <span className="text-gray-500">×</span>
                                      <input
                                        type="number"
                                        min="1"
                                        value={editingPrecommande.quantite}
                                        onChange={(e) => handleUpdatePrecommandeField('quantite', e.target.value)}
                                        className="w-16 px-2 py-1 border rounded text-sm"
                                      />
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={editingPrecommande.prix}
                                        onChange={(e) => handleUpdatePrecommandeField('prix', e.target.value)}
                                        className="w-20 px-2 py-1 border rounded text-sm"
                                        placeholder="Prix"
                                      />
                                      <span className="text-gray-500">€</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-gray-900">{pc.produit_nom}</span>
                                      <span className="text-gray-500">×</span>
                                      <span className="font-medium">{pc.quantite}</span>
                                      <span className={`font-bold ${displayPrice > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                        {displayPrice > 0 ? `${displayPrice.toFixed(2)}€` : '⚠️ Prix?'}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Boutons d'action - A DROITE */}
                                <div className="flex gap-1 flex-shrink-0">
                                  {isEditing ? (
                                    <>
                                      <button
                                        onClick={() => handleValidatePrecommande(pc)}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold text-sm shadow-md"
                                      >
                                        ✅ Confirmer
                                      </button>
                                      <button
                                        onClick={handleCancelEditPrecommande}
                                        className="px-2 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 text-xs"
                                      >
                                        ✕
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => handleValidatePrecommande(pc)}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold text-sm shadow-md"
                                        title="Valider"
                                      >
                                        ✅ Valider
                                      </button>
                                      <button
                                        onClick={() => handleEditPrecommande(pc)}
                                        className="px-2 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                                        title="Modifier"
                                      >
                                        ✏️
                                      </button>
                                      <button
                                        onClick={() => handleRejectPrecommande(pc)}
                                        className="px-2 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                                        title="Rejeter"
                                      >
                                        ❌
                                      </button>
                                      <button
                                        onClick={() => handleTransferToNextMission(pc)}
                                        className="px-2 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 text-xs"
                                        title="Transférer en note"
                                      >
                                        📋
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Remarque si présente */}
                              {pc.remarque_origine && (
                                <div className="mt-1 text-xs text-gray-500 italic truncate pl-28">
                                  💬 {pc.remarque_origine}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ));
              })()}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="text-6xl mb-4">🛒</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Panier vide</h3>
              <p className="text-gray-600">Aucune précommande en attente</p>
            </div>
          )}
        </div>
      )}

      {/* TAB CATALOGUE - Catalogue de produits */}
      {activeTab === 'catalogue' && (
      <div className="card mb-6 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="card-header flex justify-between items-center bg-gradient-to-r from-purple-100 to-pink-100">
          <h3 className="text-lg font-semibold text-purple-900">📋 Catalogue de Produits</h3>
          <button
            onClick={() => {
              setEditingProduct(null);
              setCatalogForm({ nom_produit: "", quantite_stock: "0", prix_achat: "" });
              setShowCatalogModal(true);
            }}
            className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
          >
            ➕ Nouveau produit
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {productCatalog.length === 0 ? (
              <div className="col-span-full text-center text-gray-500 py-4">
                Aucun produit dans le catalogue
              </div>
            ) : (
              productCatalog
                .sort((a, b) => a.nom_produit.localeCompare(b.nom_produit, 'fr', { sensitivity: 'base' }))
                .map((product) => (
                <div 
                  key={product.id} 
                  className="bg-white border border-purple-200 rounded-lg p-3 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 text-sm mb-1">
                        {product.nom_produit}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">
                        Stock: <span className="font-semibold">{product.quantite_stock || 0}</span>
                      </div>
                      <div className="space-y-0.5">
                        <div className="text-sm">
                          Prix: <span className="font-bold text-purple-600">{product.prix_achat.toFixed(2)}€</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="text-blue-600 hover:text-blue-800 text-xs"
                        title="Modifier"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteProductCatalog(product.id)}
                        className="text-red-600 hover:text-red-800 text-xs"
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      )}

      {/* Modal d'ajout */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                🛒 Achats pour le logement
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setCart([]);
                  setCartQuantities({});
                  setFormData({
                    client_id: "",
                    housing_id: "",
                    date_fourniture: getDateISOParis(),
                    consommable_nom: "",
                    quantite: "1",
                    prix: "",
                    use_catalog: true
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmitQuickOrder} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client *
                </label>
                <select
                  required
                  value={formData.client_id}
                  onChange={(e) => {
                    setFormData({...formData, client_id: e.target.value, housing_id: ""});
                  }}
                  className="form-select w-full"
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.prenom} {client.nom}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logement *
                </label>
                <select
                  required
                  value={formData.housing_id}
                  onChange={(e) => {
                    setFormData({...formData, housing_id: e.target.value});
                  }}
                  className="form-select w-full"
                  disabled={!formData.client_id}
                >
                  <option value="">Sélectionner un logement</option>
                  {housings
                    .filter(h => h.client_id === formData.client_id)
                    .map((housing) => (
                      <option key={housing.id} value={housing.id}>
                        {housing.pseudo || housing.adresse} - {housing.ville}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fourniture *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date_fourniture}
                  onChange={(e) => setFormData({...formData, date_fourniture: e.target.value})}
                  className="form-input"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  📝 Qu'est-ce que vous souhaitez acheter pour ce logement ? *
                </label>
                <textarea
                  required
                  value={formData.consommable_nom}
                  onChange={(e) => setFormData({...formData, consommable_nom: e.target.value})}
                  className="form-input w-full"
                  rows="4"
                  placeholder="Exemple : 2 papier toilette, 1 savon, 3 gel douche, 1 éponge..."
                />
                <p className="text-xs text-blue-600 mt-2">
                  ✨ Le système détectera automatiquement les produits et créera des précommandes en attente de validation
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setFormData({
                      client_id: "",
                      housing_id: "",
                      date_fourniture: getDateISOParis(),
                      consommable_nom: "",
                      quantite: "1",
                      prix: "",
                      use_catalog: true
                    });
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  ✨ Créer les précommandes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TAB HISTORIQUE - Précommandes validées regroupées par client/logement */}
      {activeTab === 'historique' && (
        <div className="space-y-4">
          {precommandesValidees.length > 0 ? (
            <>
              {/* Regrouper par MOIS puis par client/logement */}
              {(() => {
                // 1. Grouper par mois
                const byMonth = {};
                precommandesValidees.forEach(pc => {
                  const d = new Date(pc.validated_at || pc.date_creation || pc.created_at);
                  const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  if (!byMonth[monthKey]) byMonth[monthKey] = [];
                  byMonth[monthKey].push(pc);
                });

                const now = new Date();
                const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const sortedMonths = Object.keys(byMonth).sort((a, b) => b.localeCompare(a));

                return sortedMonths.map(monthKey => {
                  const isCurrentMonth = monthKey === currentMonthKey;
                  const monthDate = new Date(monthKey + '-01');
                  const monthLabel = monthDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                  const monthItems = byMonth[monthKey];
                  const monthTotal = monthItems.reduce((s, pc) => s + (pc.prix_estime || 0) * (pc.quantite || 1), 0);

                  // 2. Grouper par logement dans ce mois
                  const grouped = monthItems.reduce((acc, pc) => {
                    const key = `${pc.client_id}_${pc.housing_id}`;
                    if (!acc[key]) {
                      acc[key] = {
                        client: pc.client || clients.find(c => c.id === pc.client_id),
                        housing: pc.housing || housings.find(h => h.id === pc.housing_id),
                        items: []
                      };
                    }
                    acc[key].items.push(pc);
                    return acc;
                  }, {});

                  return (
                    <details key={monthKey} open={isCurrentMonth} className="group">
                      <summary className="cursor-pointer bg-gray-800 text-white px-4 py-3 rounded-lg flex justify-between items-center hover:bg-gray-700 transition-colors">
                        <span className="font-bold capitalize">{monthLabel}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-300">{monthItems.length} produit(s)</span>
                          <span className="font-bold text-green-400">{monthTotal.toFixed(2)}€</span>
                          <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                        </div>
                      </summary>
                      <div className="mt-2 space-y-3">
                        {Object.entries(grouped).map(([key, group]) => (
                          <div key={key} className="bg-white rounded-lg shadow-sm border border-green-200 overflow-hidden">
                            {/* En-tête client/logement */}
                            <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="font-bold text-white">
                                    🏠 {group.housing ? (group.housing.pseudo || group.housing.adresse) : 'Logement inconnu'}
                                  </h3>
                                  <p className="text-green-100 text-sm">
                                    👤 {group.client ? `${group.client.prenom} ${group.client.nom}` : 'Client inconnu'}
                                  </p>
                                </div>
                                <span className="bg-white text-green-600 px-2 py-1 rounded-full text-xs font-bold">
                                  {group.items.length} produit(s)
                                </span>
                              </div>
                            </div>

                            {/* Liste compacte des produits validés */}
                            <div className="divide-y divide-gray-100">
                              {group.items
                                .sort((a, b) => new Date(b.validated_at || b.created_at) - new Date(a.validated_at || a.created_at))
                                .map((pc) => (
                                  <div key={pc.id} className="px-3 py-2 hover:bg-green-50 flex items-center gap-2">
                                  <div className="flex flex-col text-xs text-gray-400 w-16 flex-shrink-0">
                                    <span>{(pc.validated_at || pc.date_creation || pc.created_at) ? new Date(pc.validated_at || pc.date_creation || pc.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}</span>
                                    {pc.added_by && <span className="text-[10px] text-purple-500">{pc.added_by}</span>}
                                  </div>
                                    <span className="flex-1 font-medium text-gray-900 truncate">{pc.produit_nom}</span>
                                    <span className="text-gray-500 text-sm">×{pc.quantite}</span>
                                    <span className="font-bold text-green-600 w-16 text-right">{(pc.prix_estime || 0).toFixed(2)}€</span>
                                    <div className="flex gap-1 flex-shrink-0">
                                      <button onClick={() => handleRevertToCart(pc)} className="px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600" title="Remettre dans le panier">🛒</button>
                                      <button onClick={() => handleCancelValidation(pc)} className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600" title="Annuler">✕</button>
                                    </div>
                                  </div>
                                ))}
                            </div>

                            {/* Total du logement */}
                            <div className="bg-green-50 px-3 py-2 border-t border-green-200">
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-green-700">Total:</span>
                                <span className="font-bold text-green-700">{group.items.reduce((s, pc) => s + (pc.prix_estime || 0) * (pc.quantite || 1), 0).toFixed(2)}€</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  );
                });
              })()}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Historique vide</h3>
              <p className="text-gray-600">Aucune précommande validée</p>
            </div>
          )}
        </div>
      )}

      {/* Modal de gestion du catalogue */}
      {showCatalogModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-purple-900">
                {editingProduct ? '✏️ Modifier le produit' : '➕ Nouveau produit au catalogue'}
              </h3>
              <button
                onClick={() => {
                  setShowCatalogModal(false);
                  setEditingProduct(null);
                  setCatalogForm({ nom_produit: "", quantite_stock: "0", prix_achat: "" });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <form onSubmit={editingProduct ? handleUpdateProductCatalog : handleAddProductToCatalog} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du produit *
                </label>
                <input
                  type="text"
                  required
                  value={catalogForm.nom_produit}
                  onChange={(e) => setCatalogForm({...catalogForm, nom_produit: e.target.value})}
                  className="form-input"
                  placeholder="Ex: Liquide vaisselle, Éponges..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantité en stock
                </label>
                <input
                  type="number"
                  min="0"
                  value={catalogForm.quantite_stock}
                  onChange={(e) => setCatalogForm({...catalogForm, quantite_stock: e.target.value})}
                  className="form-input"
                  placeholder="Ex: 10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prix (€) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={catalogForm.prix_achat}
                  onChange={(e) => setCatalogForm({...catalogForm, prix_achat: e.target.value})}
                  className="form-input"
                  placeholder="Ex: 2.00"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Prix unique (achat = vente)
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCatalogModal(false);
                    setEditingProduct(null);
                    setCatalogForm({ nom_produit: "", quantite_stock: "0", prix_achat: "" });
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary bg-purple-600 hover:bg-purple-700">
                  {editingProduct ? '✅ Modifier' : '✅ Ajouter au catalogue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails/modification de commande */}
      {showCommandeModal && selectedCommande && (
        <div className="modal-overlay">
          <div className="modal-content max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                📋 Détails de la commande #{selectedCommande.numero_commande}
              </h3>
              <button
                onClick={() => {
                  setShowCommandeModal(false);
                  setSelectedCommande(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateCommande} className="space-y-4">
              {/* Informations client et logement */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Client :</span>
                    <p className="font-semibold text-gray-900">
                      {clients.find(c => c.id === selectedCommande.client_id)?.prenom} {clients.find(c => c.id === selectedCommande.client_id)?.nom || 'Inconnu'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Logement :</span>
                    <p className="font-semibold text-gray-900">
                      {housings.find(h => h.id === selectedCommande.housing_id)?.pseudo || '-'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    📅 Date de commande
                  </label>
                  <input
                    type="date"
                    value={selectedCommande.date_commande?.split('T')[0] || ''}
                    onChange={(e) => setSelectedCommande({
                      ...selectedCommande,
                      date_commande: e.target.value
                    })}
                    className="form-input"
                    disabled={selectedCommande.statut !== 'en_cours'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🏷️ Statut
                  </label>
                  <select
                    value={selectedCommande.statut || 'en_cours'}
                    onChange={(e) => setSelectedCommande({
                      ...selectedCommande,
                      statut: e.target.value
                    })}
                    className="form-select"
                  >
                    <option value="en_cours">⏳ En cours</option>
                    <option value="validee">✓ Validée</option>
                    <option value="facturee">✅ Facturée</option>
                    <option value="annulee">❌ Annulée</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📦 Produits commandés
                </label>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {selectedCommande.items && selectedCommande.items.length > 0 ? (
                    selectedCommande.items.map((item, index) => (
                      <div key={index} className="py-3 border-b border-gray-300 last:border-b-0">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">{item.product_nom || item.nom}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newItems = selectedCommande.items.filter((_, i) => i !== index);
                              const newTotal = newItems.reduce((sum, it) => sum + (it.prix_unitaire * it.quantite), 0);
                              setSelectedCommande({
                                ...selectedCommande,
                                items: newItems,
                                montant_total_ht: newTotal,
                                montant_total_ttc: newTotal
                              });
                            }}
                            className="text-red-600 hover:text-red-800 ml-2"
                            title="Supprimer ce produit"
                          >
                            🗑️
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-600">Quantité :</label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantite}
                              onChange={(e) => {
                                const newQuantite = parseInt(e.target.value) || 1;
                                const newItems = [...selectedCommande.items];
                                newItems[index].quantite = newQuantite;
                                const newTotal = newItems.reduce((sum, it) => sum + (it.prix_unitaire * it.quantite), 0);
                                setSelectedCommande({
                                  ...selectedCommande,
                                  items: newItems,
                                  montant_total_ht: newTotal,
                                  montant_total_ttc: newTotal
                                });
                              }}
                              className="form-input w-20 text-center"
                            />
                            <span className="text-sm text-gray-600">× {item.prix_unitaire.toFixed(2)}€</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-900">{(item.prix_unitaire * item.quantite).toFixed(2)}€</div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-center py-4">
                      Aucun produit dans cette commande
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900 text-lg">Total TTC :</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {selectedCommande.montant_total_ttc?.toFixed(2) || '0.00'}€
                  </span>
                </div>
              </div>

              {selectedCommande.source === 'precommande' && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    ℹ️ Cette commande provient d'une précommande validée
                  </p>
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div>
                  {selectedCommande.statut !== 'facturee' && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          // Changer le statut à FACTUREE
                          const updated = { ...selectedCommande, statut: 'facturee' };
                          await axios.put(`${API}/commandes/${selectedCommande.id}`, updated);
                          toast.success("Commande facturée avec succès");
                          setShowCommandeModal(false);
                          setSelectedCommande(null);
                          await loadData();
                        } catch (error) {
                          console.error('Erreur facturation:', error);
                          toast.error("Erreur lors de la facturation");
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      💰 Facturer
                    </button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCommandeModal(false);
                      setSelectedCommande(null);
                    }}
                    className="btn-secondary"
                  >
                    Fermer
                  </button>
                  <button type="submit" className="btn-primary">
                    ✅ Mettre à jour
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de consommable */}
      {showEditConsumableModal && editingConsumable && (
        <div className="modal-overlay">
          <div className="modal-content max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                ✏️ Modifier le consommable
              </h3>
              <button
                onClick={() => {
                  setShowEditConsumableModal(false);
                  setEditingConsumable(null);
                  setFormData({
                    client_id: "",
                    housing_id: "",
                    date_fourniture: getDateISOParis(),
                    consommable_nom: "",
                    quantite: "1",
                    prix: "",
                    use_catalog: true
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleUpdateConsumable} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fourniture *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date_fourniture}
                  onChange={(e) => setFormData({...formData, date_fourniture: e.target.value})}
                  className="form-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du produit *
                </label>
                <input
                  type="text"
                  required
                  value={formData.consommable_nom}
                  onChange={(e) => setFormData({...formData, consommable_nom: e.target.value})}
                  className="form-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantité *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.quantite}
                    onChange={(e) => setFormData({...formData, quantite: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix (€) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.prix}
                    onChange={(e) => setFormData({...formData, prix: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditConsumableModal(false);
                    setEditingConsumable(null);
                    setFormData({
                      client_id: "",
                      housing_id: "",
                      date_fourniture: getDateISOParis(),
                      consommable_nom: "",
                      quantite: "1",
                      prix: "",
                      use_catalog: true
                    });
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  ✅ Modifier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails/modification de commande */}
      {showCommandeModal && selectedCommande && (
        <div className="modal-overlay">
          <div className="modal-content max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                📋 Détails de la commande #{selectedCommande.numero_commande}
              </h3>
              <button
                onClick={() => {
                  setShowCommandeModal(false);
                  setSelectedCommande(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleUpdateCommande} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de commande
                  </label>
                  <input
                    type="date"
                    value={selectedCommande.date_commande?.split('T')[0] || ''}
                    onChange={(e) => setSelectedCommande({
                      ...selectedCommande,
                      date_commande: e.target.value
                    })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select
                    value={selectedCommande.statut || 'en_cours'}
                    onChange={(e) => setSelectedCommande({
                      ...selectedCommande,
                      statut: e.target.value
                    })}
                    className="form-select"
                  >
                    <option value="en_cours">⏳ En cours</option>
                    <option value="validee">✓ Validée</option>
                    <option value="facturee">✅ Facturée</option>
                    <option value="annulee">❌ Annulée</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Produits commandés
                </label>
                <div className="bg-gray-50 rounded-lg p-4">
                  {selectedCommande.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                      <div>
                        <span className="font-medium">{item.produit_nom}</span>
                        <span className="text-gray-600 ml-2">x{item.quantite}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{(item.prix_unitaire * item.quantite).toFixed(2)}€</div>
                        <div className="text-sm text-gray-600">{item.prix_unitaire.toFixed(2)}€/unité</div>
                      </div>
                    </div>
                  )) || (
                    <div className="text-gray-500 text-center py-4">
                      Aucun produit dans cette commande
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total TTC :</span>
                  <span className="text-xl font-bold text-blue-600">
                    {selectedCommande.montant_total_ttc?.toFixed(2) || '0.00'}€
                  </span>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCommandeModal(false);
                    setSelectedCommande(null);
                  }}
                  className="btn-secondary"
                >
                  Fermer
                </button>
                {selectedCommande.statut === 'en_cours' && (
                  <button type="submit" className="btn-primary">
                    ✅ Mettre à jour
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockClients;
